import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { Plus, ExternalLink, Copy, Radio, Languages } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function Webinars() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translatingId, setTranslatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/webinars");
    setRows(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const copyPublic = (slug) => {
    const url = `${window.location.origin}/w/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Landing page link copied");
  };

  const translateToHi = async (wid) => {
    setTranslatingId(wid);
    try {
      await api.post(`/webinars/${wid}/translate`, { target_lang: "hi" });
      toast.success("Landing page translated to Hindi. Toggle available on the public page.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setTranslatingId(null); }
  };

  return (
    <div className="space-y-6 vp-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="vp-label">Webinars</div>
          <h1 className="font-display text-3xl font-bold mt-1">Your webinars</h1>
        </div>
        <Link to="/app/webinars/new" data-testid={TIDS.webinarNewBtn} className="vp-btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New webinar
        </Link>
      </div>

      <div className="vp-glass p-2 sm:p-4 overflow-x-auto">
        <table className="vp-table min-w-[720px]">
          <thead>
            <tr>
              <th>Title</th>
              <th>Date</th>
              <th>Price</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} className="text-[#747680]">Loading…</td></tr>)}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-[#4A4D57]">
                No webinars yet. <Link to="/app/webinars/new" className="text-[#0B5CFF] hover:underline">Create your first one →</Link>
              </td></tr>
            )}
            {rows.map((w) => (
              <tr key={w.id} className="hover:bg-[#F6F7FA] transition-colors">
                <td>
                  <Link to={`/app/webinars/${w.id}`} className="text-[#0B0D17] font-medium hover:text-[#0B5CFF]">{w.title}</Link>
                  <div className="text-xs text-[#747680] mt-0.5 truncate max-w-xs">{w.description}</div>
                </td>
                <td className="text-[#4A4D57]">{new Date(w.scheduled_at).toLocaleString("en-IN")}</td>
                <td className="text-[#4A4D57]">{w.price > 0 ? fmtINR(w.price) : "Free"}</td>
                <td>
                  <span className={"vp-chip " + (w.status === "published" ? "text-[#0B5CFF]" : "")}>{w.status}</span>
                </td>
                <td>
                  <div className="flex justify-end gap-2 flex-wrap">
                    <Link to={`/app/live/${w.id}`} className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3" title="Live Q&A panel" data-testid={"webinar-live-" + w.id}>
                      <Radio className="w-3.5 h-3.5" /> Live
                    </Link>
                    <button
                      onClick={() => translateToHi(w.id)}
                      disabled={translatingId === w.id}
                      className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3"
                      title="Translate landing page to Hindi"
                      data-testid={TIDS.translateBtn + "-" + w.id}
                    >
                      <Languages className="w-3.5 h-3.5" /> {translatingId === w.id ? "…" : "हिन्दी"}
                    </button>
                    <button data-testid={TIDS.webinarCopyLink} onClick={() => copyPublic(w.slug)} className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3">
                      <Copy className="w-3.5 h-3.5" /> Link
                    </button>
                    <a href={`/w/${w.slug}`} target="_blank" rel="noreferrer" className="vp-btn-ghost text-xs inline-flex items-center gap-1 py-1.5 px-3">
                      <ExternalLink className="w-3.5 h-3.5" /> Preview
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
